import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaGoogle, FaApple, FaEnvelope } from 'react-icons/fa';
import { Icon } from '../common/Icon';
import EmailSignInForm from './EmailSignInForm';
import { authService } from '../../services/auth';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: rgb(31, 31, 31);
  padding: 2rem;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const Title = styled.h2`
  color: white;
  margin: 0 0 1.5rem 0;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: white;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const SignInButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: background-color 0.2s;

  &.google {
    background-color: white;
    color: #333;

    &:hover {
      background-color: #f0f0f0;
    }
  }

  &.apple {
    background-color: black;
    color: white;

    &:hover {
      background-color: #333;
    }
  }

  &.email {
    background-color: #4285F4;
    color: white;

    &:hover {
      background-color: #357ABD;
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: white;
  margin: 1.5rem 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
`;

const HelperText = styled.p`
  margin: -0.25rem 0 1rem 0;
  color: #ff6b6b;
  font-size: 0.85rem;
  text-align: center;
`;

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SignInModal: React.FC<SignInModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [currentMode, setCurrentMode] = useState<'signin' | 'signup' | 'forgot'>('signin');
  const [googleSignInFailed, setGoogleSignInFailed] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleClose = () => {
    setShowEmailForm(false);
    setCurrentMode('signin');
    onClose();
  };

  const handleModeChange = (mode: 'signin' | 'signup' | 'forgot') => {
    setCurrentMode(mode);
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleSignInFailed(false);
      const result = await authService.signInWithGoogle();
      onSuccess();
      handleClose();

      // Redirect new users to subscription page
      if (result.isNewUser) {
        // Track Lead event for new user signup
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Google Sign Up',
            content_category: 'signup'
          });
        }

        // Track Google Analytics event
        if (window.gtag) {
          window.gtag('event', 'sign_up', {
            method: 'Google'
          });
        }

        navigate('/subscription');
      }
    } catch (error: any) {
      // Only log error if it's not a timeout/cancel (user might have signed in successfully anyway)
      if (error?.message !== 'Sign-in timeout or cancelled' && error?.message !== 'Sign-in cancelled') {
        console.error('Google sign in error:', error);
      }
      if (error?.message === 'User not signed in to Google') {
        setGoogleSignInFailed(true);
      }

      // Don't show error to user for timeout/cancel since they might have successfully signed in
    }
  };

  const handleAppleSignIn = async () => {
    try {
      // Load Apple Sign-In script if not already loaded
      if (!window.AppleID) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const result = await authService.signInWithApple();
      onSuccess();
      handleClose();

      // Redirect new users to subscription page
      if (result.isNewUser) {
        // Track Lead event for new user signup
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Apple Sign Up',
            content_category: 'signup'
          });
        }

        // Track Google Analytics event
        if (window.gtag) {
          window.gtag('event', 'sign_up', {
            method: 'Apple'
          });
        }

        navigate('/subscription');
      }
    } catch (error) {
      console.error('Apple sign in error:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEmailSuccess = () => {
    onSuccess();
    handleClose();
    //navigate('/you');
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>&times;</CloseButton>
        <Title>
          {currentMode === 'signup' ? 'Sign Up' : 
           currentMode === 'forgot' ? 'Reset Password' : 'Sign In'}
        </Title>
        {!showEmailForm ? (
          <>
            <SignInButton className="google" onClick={handleGoogleSignIn}>
              <Icon icon={FaGoogle} size={20} />
              Continue with Google
            </SignInButton>
            {googleSignInFailed && (
              <HelperText>Please sign in to your Google account in this browser and try again.</HelperText>
            )}
            <SignInButton className="apple" onClick={handleAppleSignIn}>
              <Icon icon={FaApple} size={20} />
              Continue with Apple
            </SignInButton>
            <Divider>or</Divider>
            <SignInButton className="email" onClick={() => setShowEmailForm(true)}>
              <Icon icon={FaEnvelope} size={20} />
              Continue with Email
            </SignInButton>
          </>
        ) : (
          <EmailSignInForm 
            onSuccess={handleEmailSuccess} 
            onError={(error) => console.error(error)}
            onModeChange={handleModeChange}
          />
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default SignInModal; 